import UnreadChatBadge from '../UnreadChatBadge';
import EventMediaPanel from '../../home_media_ui/EventMediaPanel';
import AdditionalMeasurementPanel from './AdditionalMeasurementPanel';
import { useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, IconButton,
  Modal, ModalClose, ModalDialog, Sheet, Stack, Tooltip, Typography,
} from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";

import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import DesignServicesRoundedIcon from '@mui/icons-material/DesignServices';
import StraightenRoundedIcon from '@mui/icons-material/StraightenRounded';
import SquareFootRoundedIcon from '@mui/icons-material/SquareFootRounded';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import { APIQualityEventDetail } from "../../api";
import StarPicker from "./StarPicker";
import ReviewHistory from "./ReviewHistory";
import { fmtDateTime, fmtWaiting, qaMeta, waitingColor } from "./qualityTheme";

/**
 * Компактная карточка события для контролёра.
 *
 * Зачем своя, а не общая модалка события: та тяжёлая, грузится лениво и
 * работает по УЖЕ загруженному списку событий. У контролёра этого списка нет —
 * его раздел живёт на своих данных, и кнопка честно отвечала «событие ещё не
 * загружено». Здесь всё приходит одним запросом по нажатию, и только то, по
 * чему реально сверяют чертёж.
 *
 * props:
 *   open, eventId, token, onClose
 *   onApprove(stars, currentCard) → {status}; onRework(currentCard)
 *   Действия используют загруженный круг, а не старую строку списка.
 *   onChat(chatId)
 */
export default function QualityEventModal({
  open, eventId, token, onClose, onApprove, onRework, onChat,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [stars, setStars] = useState(0);
  const [busy, setBusy] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [mediaViewing, setMediaViewing] = useState(false);
  const loadEpoch = useRef(0);

  const load = async (clearActionError = true) => {
    if (!eventId) return;
    const epoch = ++loadEpoch.current;
    setLoading(true); setError('');
    if (clearActionError) setActionError('');
    try {
      const res = await APIQualityEventDetail(eventId, token);
      if (epoch !== loadEpoch.current) return;
      if (res?.status !== 'ok') { setError(res?.message || res?.detail || 'Не удалось загрузить'); return; }
      setData(res.event);
    } catch (failure) {
      if (epoch === loadEpoch.current) setError('Не удалось обновить событие. Проверьте связь.');
    } finally {
      if (epoch === loadEpoch.current) setLoading(false);
    }
  };

  useEffect(() => {
    setData(null); setMediaViewing(false); setActionError('');
    if (!open) { loadEpoch.current += 1; setError(''); setStars(0); setHistOpen(false); return undefined; }
    load();
    return () => {loadEpoch.current += 1;};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventId, token]);

  const meta = qaMeta(data?.qa_state);
  const pending = data?.qa_state === 'pending';
  const canReview = pending && data?.can_review !== false && !error;
  const waiting = fmtWaiting(data?.waiting_hours);
  useEffect(() => { setStars(0); }, [data?.id, data?.qa_round]);

  const approve = async () => {
    if (busy || !canReview || loading) return;
    setBusy(true); setActionError('');
    try {
      const result = await onApprove?.(stars, data);
      if (result?.status === 'ok') onClose?.();
      else {
        setActionError(result?.message || result?.detail || 'Не удалось принять работу. Проверьте статус и повторите.');
        await load(false);
      }
    } catch (failure) {
      setActionError('Не удалось отправить решение. Событие обновлено, проверьте его статус.');
      await load(false);
    } finally { setBusy(false); }
  };

  return (
    <Modal open={!!open} disableEnforceFocus={mediaViewing} onClose={busy ? undefined : onClose}
      sx={{ zIndex: 10108, display: 'flex', alignItems: 'center', justifyContent: 'center',
        '--qa-modal-gap': { xs: '8px', sm: '24px' }, p: 'var(--qa-modal-gap)', overflow: 'hidden' }}
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(5px)' } } }}>
      <ModalDialog variant="outlined" aria-label="Проверка события" sx={{
          width: 680, maxWidth: '100%', p: 0, overflow: 'hidden', borderRadius: '20px',
          position: 'relative', inset: 'auto', transform: 'none', m: 0,
          maxHeight: 'calc(100dvh - 2 * var(--qa-modal-gap))', minHeight: 0,
          display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(15,23,42,.22)',
        }}>
          <Box component={motion.div}
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            sx={{display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden'}}
          >
            {/* ── Шапка ── */}
            <Box sx={{
              px: { xs: 1.5, sm: 2 }, py: 1.5, flexShrink: 0,
              background: `linear-gradient(135deg, ${meta.soft}, transparent 70%)`,
              borderBottom: '1px solid', borderColor: 'divider',
            }}>
              <ModalClose disabled={busy} />
              <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap', pr: 4, rowGap: 0.5 }}>
                <Typography level="title-md" sx={{ fontWeight: 800 }}>
                  №{data?.deal_id ?? eventId}
                </Typography>
                <Chip size="sm" variant="soft"
                  sx={{ bgcolor: meta.soft, color: meta.text, fontWeight: 700,
                        '--Chip-minHeight': '20px', fontSize: '0.66rem' }}>
                  {meta.label}
                </Chip>
                {data?.qa_round > 1 && (
                  <Chip size="sm" variant="outlined"
                    sx={{ '--Chip-minHeight': '20px', fontSize: '0.66rem' }}>
                    круг {data.qa_round}
                  </Chip>
                )}
                {waiting && (
                  <Chip size="sm" variant="soft" color={waitingColor(data?.waiting_hours)}
                    startDecorator={<AccessTimeRoundedIcon sx={{ fontSize: 12 }} />}
                    sx={{ '--Chip-minHeight': '20px', fontSize: '0.66rem' }}>
                    ждёт {waiting}
                  </Chip>
                )}
              </Stack>
              {data && (
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.5 }}>
                  <PlaceRoundedIcon sx={{ fontSize: 14, color: 'text.tertiary', flexShrink: 0 }} />
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    {[data.city, data.address].filter(Boolean).join(', ') || 'без адреса'}
                  </Typography>
                </Stack>
              )}
            </Box>

            {/* ── Тело ── */}
            <Box data-qa-body sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5, flex: '1 1 auto', minHeight: 0,
              overflowY: 'auto', overscrollBehavior: 'contain', scrollbarGutter: 'stable', overflowWrap: 'anywhere' }}>
              {loading && (
                <Stack sx={{ alignItems: 'center', py: 4 }} spacing={1.5}>
                  <CircularProgress size="sm" />
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>Загружаем событие…</Typography>
                </Stack>
              )}

              {!loading && error && (
                <Alert color="danger" variant="soft"
                  endDecorator={
                    <Button size="sm" variant="soft" color="danger"
                      startDecorator={<RefreshRoundedIcon />} onClick={load}>
                      Ещё раз
                    </Button>
                  }>
                  <Typography level="body-sm">{error}</Typography>
                </Alert>
              )}

              {!loading && data && (
                <>
                  {data.media_access?.read && <EventMediaPanel eventId={eventId} token={token}
                    initialSection="drawings" showStorageLink={!!data.media_access?.storage_link} onViewerChange={setMediaViewing}
                    title="Материалы для проверки" />}

                  {/* Кто и что */}
                  <Box sx={{
                    display: 'grid', gap: 1, mb: 1.5,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))' },
                  }}>
                    <Info icon={<DesignServicesRoundedIcon sx={{ fontSize: 15 }} />}
                          label="Чертёжник" value={data.draftsman_full?.name || '—'} />
                    <Info icon={<StraightenRoundedIcon sx={{ fontSize: 15 }} />}
                          label="Обмерщик" value={data.surveyor?.name || '—'} />
                    <Info icon={<SquareFootRoundedIcon sx={{ fontSize: 15 }} />}
                          label="Площадь чертёжника"
                          value={data.square ? `${data.square} м²` : '—'}
                          hint={data.recovery_mode ? `расчётная площадь ${data.billing_square} м²; без перерасчёта`
                            : data.deal_square ? `в Bitrix ${data.deal_square} м²` : null} />
                    <Info icon={<PersonRoundedIcon sx={{ fontSize: 15 }} />}
                          label="Менеджер" value={data.manager_full?.name || '—'} />
                    <Info icon={<AccessTimeRoundedIcon sx={{ fontSize: 15 }} />}
                          label="Сдан на проверку" value={fmtDateTime(data.qa_sent_at) || '—'} />
                    <Info icon={<AccessTimeRoundedIcon sx={{ fontSize: 15 }} />}
                          label="Дедлайн черчения"
                          value={fmtDateTime(data.drawing_deadline) || '—'}
                          danger={data.is_overdue} />
                  </Box>

                  {data.recovery_mode && Object.keys(data.actual_service_inputs || {}).length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography level="title-sm">Фактические значения услуг повторного чертежа</Typography>
                      {Object.entries(data.actual_service_inputs).map(([id, value]) => (
                        <Typography level="body-sm" key={id}>{data.services?.find(service => String(service.id) === id)?.title || `Услуга №${id}`}: {value}</Typography>
                      ))}
                    </Box>
                  )}
                  {!!(data.program || []).length && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.5 }}>
                        Программа
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                        {data.program.map((p, i) => (
                          <Chip key={i} size="sm" variant="soft">{p}</Chip>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {!!(data.services || []).length && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.5 }}>
                        Доп. услуги — их тоже надо проверить
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                        {data.services.map((s) => (
                          <Chip key={s.id} size="sm" variant="soft" color="warning">
                            {s.title}
                            {s.input_value != null ? ` · ${s.input_value}` : ''}
                          </Chip>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  <CommentBlock title="Комментарий обмерщика" text={data.surveyor_comment} />
                  <CommentBlock title="Комментарий чертёжника" text={data.draftsman_comment} />
                  <CommentBlock title="Комментарий менеджера" text={data.manager_comment} />

                  {/* История проверок */}
                  {!!(data.reviews || []).length && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Box onClick={() => setHistOpen((v) => !v)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', py: 0.5 }}>
                        <HistoryRoundedIcon sx={{ fontSize: 15, color: 'text.tertiary' }} />
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', flex: 1 }}>
                          История проверок · {data.reviews.length}
                        </Typography>
                        <Box component={motion.span}
                          animate={{ rotate: histOpen ? 180 : 0 }}
                          transition={{ duration: 0.18 }}
                          sx={{ lineHeight: 0, color: 'text.tertiary', fontSize: 12 }}>▾</Box>
                      </Box>
                      <AnimatePresence initial={false}>
                        {histOpen && (
                          <Box component={motion.div}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.22, ease: 'easeInOut' }}
                            sx={{ overflow: 'hidden', pt: 1 }}
                          >
                            <ReviewHistory reviews={data.reviews} compact />
                          </Box>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                  {data.client_rejected && <Alert color="danger" sx={{mt: 1.5, p: 1.25, borderRadius: '10px'}}>
                    <Stack spacing={0.5}><Typography level="title-sm">Клиент не принял работу</Typography>
                    <Typography level="body-sm" sx={{overflowWrap: 'anywhere'}}>{data.client_rejection_reason}</Typography></Stack>
                  </Alert>}
                  {(data.additional_measurement || ['quality_check', 'in_drawing', 'ready_for_delivery', 'closed_by_surveyor'].includes(data.kanban_stage)) &&
                    <Box sx={{mt: 1.5}}><AdditionalMeasurementPanel compact eventId={eventId} cycle={data.additional_measurement} token={token}
                      canStart={['quality_check', 'in_drawing', 'ready_for_delivery', 'closed_by_surveyor'].includes(data.kanban_stage)}
                      onChanged={(event) => setData(prev => ({ ...prev, ...event }))} /></Box>}
                </>
              )}
            </Box>

            {!loading && pending && data?.can_review === false && <Alert color="warning" sx={{ mx: { xs: 1.5, sm: 2 }, my: 1, flexShrink: 0 }}>
              {data.review_blocker || 'Состояние события изменилось. Обновите карточку.'}
            </Alert>}
            {actionError && <Alert color="danger" sx={{ mx: { xs: 1.5, sm: 2 }, my: 1, flexShrink: 0 }}>
              <Typography level="body-sm">{actionError}</Typography>
            </Alert>}
            {/* ── Подвал с действиями ── */}
            {!loading && data && (
              <>
                <Divider sx={{flexShrink: 0}} />
                <Box data-qa-actions sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, bgcolor: 'background.surface', flexShrink: 0 }}> 
                  {pending ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}
                      sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
                      <StarPicker value={stars} onChange={setStars} size={24} />
                      <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', gap: 0.75, '& > button': {flex: {xs: 1, sm: 'initial'}}}}>
                        {data.chat_id && (
                          <Tooltip size="sm" variant="soft" title="Чат разбора с чертёжником">
                            <IconButton size="sm" variant="soft" color="primary"
                              onClick={() => onChat?.(data.chat_id)}>
                              <UnreadChatBadge chatId={data.chat_id}><ChatBubbleRoundedIcon sx={{ fontSize: 16 }} /></UnreadChatBadge>
                            </IconButton>
                          </Tooltip>
                        )}
                        <Button size="sm" variant="soft" color="danger" disabled={busy || !canReview}
                          startDecorator={<ReplayRoundedIcon />}
                          onClick={() => { onRework?.(data); onClose?.(); }}>
                          На доработку
                        </Button>
                        <Button size="sm" color="success" loading={busy} disabled={!canReview}
                          startDecorator={!busy ? <CheckCircleRoundedIcon /> : null}
                          onClick={approve}>
                          Принять
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        {meta.label}
                        {data.kanban_stage === 'ready_for_delivery' ? ' · готово к выдаче' : ''}
                      </Typography>
                      {data.chat_id && (
                        <Button size="sm" variant="soft" color="primary"
                          startDecorator={<UnreadChatBadge chatId={data.chat_id}><ChatBubbleRoundedIcon sx={{ fontSize: 15 }} /></UnreadChatBadge>}
                          onClick={() => onChat?.(data.chat_id)}>
                          Чат разбора
                        </Button>
                      )}
                    </Stack>
                  )}
                </Box>
              </>
            )}
          </Box>
        </ModalDialog>
    </Modal>
  );
}


function Info({ icon, label, value, hint, danger }) {
  return (
    <Sheet variant="outlined" sx={{ p: 1, borderRadius: '10px', minWidth: 0 }}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.tertiary', mb: 0.25 }}>
        {icon}
        <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>{label}</Typography>
      </Stack>
      <Typography level="body-sm" sx={{ fontWeight: 700, color: danger ? 'danger.500' : 'text.primary' }} noWrap>
        {value}
      </Typography>
      {hint && (
        <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>{hint}</Typography>
      )}
    </Sheet>
  );
}

function CommentBlock({ title, text }) {
  if (!text || !String(text).trim()) return null;
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.25 }}>{title}</Typography>
      <Sheet variant="soft" sx={{ p: 1, borderRadius: '10px' }}>
        <Typography level="body-sm" sx={{ whiteSpace: 'pre-line' }}>{text}</Typography>
      </Sheet>
    </Box>
  );
}
